import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { LucideFileText, LucideTrash, LucideUploadCloud } from '@lucide/angular';
import { FieldBase } from '../shared/field-base';
import { FieldErrorComponent } from '../shared/field-error.component';

export interface MultiFileItem {
  id: string;
  file: File;
}

/**
 * Multi-file upload with drag & drop, per-file size validation, and max-files cap.
 * Value: MultiFileItem[] (controlled externally so callers can manage add/remove).
 */
@Component({
  selector: 'ds-multi-file-upload',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, FieldErrorComponent],
  template: `
    <div class="ds-field upload-field">
      <span class="ds-field__label" [id]="fieldId + '-label'" [class.ds-field__label--hidden]="hiddenLabel()">
        {{ label() }}
        @if (required()) {
          <span class="ds-field__required" aria-hidden="true">*</span>
        }
      </span>
      @if (helperText()) {
        <p class="ds-field__helper" [id]="fieldId + '-helper'">{{ helperText() }}</p>
      }

      <div
        class="zone"
        [class.zone--drag]="dragOver()"
        [class.zone--disabled]="disabled()"
        [class.zone--error]="shownError()"
        (dragover)="handleDragOver($event)"
        (dragleave)="dragOver.set(false)"
        (drop)="handleDrop($event)"
      >
        <span class="zone__icon" aria-hidden="true">
          <ng-container
            [ngComponentOutlet]="uploadIcon"
            [ngComponentOutletInputs]="{ size: 40, strokeWidth: 1.75 }"
          />
        </span>
        <p class="zone__hint">Tarik berkas ke sini, atau</p>
        <button
          type="button"
          class="zone__browse"
          [disabled]="disabled() || atMaxFiles()"
          [attr.aria-describedby]="fieldId + '-label ' + (describedBy() ?? '')"
          (click)="fileInput.click()"
          (blur)="onTouched()"
        >
          Pilih Berkas
        </button>
        <p class="zone__meta">{{ constraintText() }}</p>
        @if (atMaxFiles()) {
          <p class="zone__warn" role="status">Maksimal {{ maxFiles() }} file tercapai.</p>
        }
        <input
          #fileInput
          class="zone__native"
          type="file"
          tabindex="-1"
          aria-hidden="true"
          [attr.accept]="accept() ?? ''"
          [disabled]="disabled()"
          multiple
          (change)="handleFileChange($event)"
        />
      </div>

      @if (shownError(); as message) {
        <ds-field-error [errorId]="fieldId + '-error'">{{ message }}</ds-field-error>
      }

      @if (files().length > 0) {
        <ul class="file-list" [attr.aria-label]="label() + ' — daftar file terpilih'">
          @for (item of files(); track item.id) {
            <li class="file-list__item">
              <span class="file-list__icon" aria-hidden="true">
                <ng-container
                  [ngComponentOutlet]="fileIcon"
                  [ngComponentOutletInputs]="{ size: 24, strokeWidth: 1.75 }"
                />
              </span>
              <div class="file-list__info">
                <span class="file-list__name">{{ item.file.name }}</span>
                <span class="file-list__size">{{ formatSize(item.file.size) }}</span>
              </div>
              <button
                type="button"
                class="file-list__remove"
                [disabled]="disabled()"
                [attr.aria-label]="'Hapus ' + item.file.name"
                (click)="removeFile(item.id)"
              >
                <ng-container
                  [ngComponentOutlet]="trashIcon"
                  [ngComponentOutletInputs]="{ size: 20, strokeWidth: 2.25 }"
                />
                Hapus
              </button>
            </li>
          }
        </ul>
      }
    </div>
  `,
  styles: `
    .ds-field__label--hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .zone {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-6) var(--space-5);
      background: var(--color-surface);
      border: 2px dashed var(--color-border-input);
      border-radius: var(--radius-lg);
      text-align: center;
      transition: border-color var(--motion-fast), background-color var(--motion-fast);

      &--drag {
        border-color: var(--color-primary);
        background: var(--color-primary-tint);
      }

      &--error {
        border-color: var(--color-error);
      }

      &--disabled {
        background: var(--color-surface-sunken);
        border-color: var(--color-border);
      }
    }

    .zone__icon {
      color: #20335D;
    }

    .zone__hint {
      margin: 0;
      color: var(--color-text-secondary);
    }

    .zone__browse {
      min-height: var(--touch-target-comfort);
      padding: 0 var(--space-5);
      border: 2px solid var(--color-primary);
      border-radius: var(--radius-full);
      background: var(--color-surface);
      color: var(--color-primary);
      font-size: var(--text-md);
      font-weight: var(--font-weight-bold);
      cursor: pointer;
      transition: background-color var(--motion-fast);

      &:hover:not(:disabled) {
        background: var(--color-primary-tint);
      }

      &:disabled {
        border-color: var(--color-border);
        color: var(--color-text-disabled);
        cursor: not-allowed;
      }
    }

    .zone__meta {
      margin: 0;
      font-size: var(--text-sm);
      color: var(--color-text-secondary);
    }

    .zone__warn {
      margin: 0;
      font-size: var(--text-sm);
      font-weight: var(--font-weight-bold);
      color: var(--color-warning-strong, var(--color-text-secondary));
    }

    .zone__native {
      display: none;
    }

    .file-list {
      list-style: none;
      margin: var(--space-3) 0 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: var(--space-2);
    }

    .file-list__item {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-2) var(--space-3);
      background: var(--color-primary-tint);
      border: 1px solid var(--color-teal-300);
      border-radius: var(--radius-md);
    }

    .file-list__icon {
      color: var(--color-primary);
      flex-shrink: 0;
    }

    .file-list__info {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .file-list__name {
      font-weight: var(--font-weight-bold);
      overflow-wrap: anywhere;
    }

    .file-list__size {
      font-size: var(--text-sm);
      color: var(--color-text-secondary);
    }

    .file-list__remove {
      display: inline-flex;
      align-items: center;
      gap: var(--space-1);
      min-height: var(--touch-target);
      padding: 0 var(--space-3);
      border: 0;
      border-radius: var(--radius-full);
      background: transparent;
      color: var(--color-error-strong);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-bold);
      cursor: pointer;
      transition: background-color var(--motion-fast);
      flex-shrink: 0;

      &:hover:not(:disabled) {
        background: var(--color-error-tint);
      }
    }
  `,
})
export class MultiFileUploadComponent extends FieldBase<MultiFileItem[]> {
  accept = input<string | undefined>(undefined);
  maxSizeMb = input(5);
  acceptDescription = input<string | undefined>(undefined);
  hiddenLabel = input(false);
  maxFiles = input(10);

  readonly filesChange = output<MultiFileItem[]>();
  readonly rejectedFiles = output<{ name: string; reason: string }[]>();

  protected readonly uploadIcon = LucideUploadCloud;
  protected readonly fileIcon = LucideFileText;
  protected readonly trashIcon = LucideTrash;

  protected readonly dragOver = signal(false);
  protected readonly sizeError = signal<string | null>(null);
  protected readonly files = signal<MultiFileItem[]>([]);

  protected readonly shownError = computed(() => this.errorText() ?? this.sizeError());

  protected readonly atMaxFiles = computed(() => this.files().length >= this.maxFiles());

  protected readonly constraintText = computed(() => {
    const parts: string[] = [];
    if (this.acceptDescription()) parts.push(this.acceptDescription()!);
    parts.push(`Maksimal ${this.maxFiles()} file`);
    parts.push(`Maksimal ${this.maxSizeMb()} MB per file`);
    return parts.join(' · ');
  });

  protected handleDragOver(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    this.dragOver.set(true);
  }

  protected handleDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(false);
    if (this.disabled()) return;
    const dropped = event.dataTransfer?.files;
    if (dropped && dropped.length > 0) {
      this.addFiles(Array.from(dropped));
    }
  }

  protected handleFileChange(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    if (inputEl.files && inputEl.files.length > 0) {
      this.addFiles(Array.from(inputEl.files));
    }
    inputEl.value = '';
  }

  protected removeFile(id: string): void {
    this.files.update((list) => list.filter((f) => f.id !== id));
    this.sizeError.set(null);
    this.emitChange();
  }

  clear(): void {
    this.files.set([]);
    this.sizeError.set(null);
  }

  protected formatSize(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  private addFiles(newFiles: File[]): void {
    this.sizeError.set(null);
    const accepted: MultiFileItem[] = [];
    const rejected: { name: string; reason: string }[] = [];

    for (const file of newFiles) {
      if (this.atMaxFiles() || accepted.length + this.files().length >= this.maxFiles()) {
        rejected.push({ name: file.name, reason: `Maksimal ${this.maxFiles()} file` });
        continue;
      }
      if (file.size > this.maxSizeMb() * 1024 * 1024) {
        rejected.push({
          name: file.name,
          reason: `Berkas terlalu besar (${this.formatSize(file.size)}). Maksimal ${this.maxSizeMb()} MB.`,
        });
        continue;
      }
      accepted.push({ id: this.genId(), file });
    }

    if (accepted.length > 0) {
      this.files.update((list) => [...list, ...accepted]);
      this.emitChange();
    }

    if (rejected.length > 0) {
      this.rejectedFiles.emit(rejected);
      if (accepted.length === 0) {
        this.sizeError.set(rejected[0].reason);
      }
    }
    this.onTouched();
  }

  private emitChange(): void {
    this.onChange(this.files());
    this.filesChange.emit(this.files());
  }

  private genId(): string {
    return `f-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
}
